import iMISFetch from "@/utils/iMISFetch";
import { IQA_SYNC_BUNDLE_PURCHASE, QUERY_ENDPOINT } from "@/store/constants";
import type { BundlePurchaseRow } from "@/store/types";

/** Reads a named property's Value out of an AGD_Coupons GenericEntityData row. */
function getProperty(row: any, name: string): unknown {
  const values = row?.Properties?.$values as { Name: string; Value: unknown }[] | undefined;
  return values?.find((p) => p.Name === name)?.Value;
}

/**
 * GET /api/query?queryname=$/ASI/Store/SyncBundlePurchase&Id={partyId}
 *
 * Returns the party's bundle-purchase rows for today, minus any row that's
 * already claimed by EITHER of two checks: (1) its CouponMessage already
 * exists among the party's own AGD_Coupons rows — the legacy check, kept
 * permanently as a safety net for coupons created before AGD_BundleUse
 * existed, so no backfill migration is needed; (2) its (OrderNumber,
 * LineNumber) already exists among the party's own AGD_BundleUse rows — the
 * authoritative check for everything created after AGD_BundleUse shipped
 * (see docs/superpowers/specs/2026-07-22-bundle-use-idempotency-design.md).
 * A row already inserted by the nightly job, or by an earlier claim, is
 * excluded here rather than re-inserted.
 */
export async function fetchUnclaimedBundlePurchases(partyId: string): Promise<BundlePurchaseRow[]> {
  const [purchaseResult, couponsResult, bundleUseResult] = await Promise.all([
    iMISFetch<BundlePurchaseRow>(QUERY_ENDPOINT, {
      query: `${IQA_SYNC_BUNDLE_PURCHASE}&Id=${partyId}`,
      limit: 0,
    }),
    iMISFetch<any>(`AGD_Coupons?id=${partyId}`, {}),
    iMISFetch<any>(`AGD_BundleUse?id=${partyId}`, {}),
  ]);

  if (!purchaseResult.success) {
    throw new Error(`fetchUnclaimedBundlePurchases failed: ${purchaseResult.message || "unknown error"}`);
  }
  if (!couponsResult.success) {
    throw new Error(
      `fetchUnclaimedBundlePurchases failed to check existing coupons: ${couponsResult.message || "unknown error"}`
    );
  }
  if (!bundleUseResult.success) {
    throw new Error(
      `fetchUnclaimedBundlePurchases failed to check existing bundle-use records: ${bundleUseResult.message || "unknown error"}`
    );
  }

  const purchaseRows = Array.isArray(purchaseResult.data) ? (purchaseResult.data as BundlePurchaseRow[]) : [];

  const existingCouponRows = Array.isArray(couponsResult.data) ? (couponsResult.data as any[]) : [];
  const existingMessages = new Set(existingCouponRows.map((row) => getProperty(row, "CouponMessage")));

  const existingBundleUseRows = Array.isArray(bundleUseResult.data) ? (bundleUseResult.data as any[]) : [];
  const existingOrderLines = new Set(
    existingBundleUseRows.map((row) => `${getProperty(row, "OrderNumber")}|${getProperty(row, "LineNumber")}`)
  );

  return purchaseRows.filter(
    (row) => !existingMessages.has(row.CouponMessage) && !existingOrderLines.has(`${row.OrderNumber}|${row.LineNumber}`)
  );
}

/** Exercise-count per bundle ProductCode — the IQA does not return this (no reliable source). */
const MAX_USES_BY_PRODUCT_CODE: Record<string, number> = {
  GD3EXB: 3,
  GD6EXB: 6,
  GD8EXSPC: 8,
  GD12EXB: 12,
  IMP2EXB: 2,
  IMP4EXB: 4,
};

/** 8-character uppercase hex code — same entropy class as the SQL job's NEWID()-derived code. */
function generateCouponCode(): string {
  let code = "";
  for (let i = 0; i < 8; i++) {
    code += Math.floor(Math.random() * 16).toString(16);
  }
  return code.toUpperCase();
}

function property(name: string, value: unknown) {
  return { $type: "Asi.Soa.Core.DataContracts.GenericPropertyData, Asi.Contracts", Name: name, Value: value };
}

/** Total AGD_BundleUse write attempts per claim: 1 initial + 2 retries. */
const BUNDLE_USE_WRITE_ATTEMPTS = 3;

/**
 * POSTs the AGD_BundleUse row marking this order line as claimed, retrying
 * up to BUNDLE_USE_WRITE_ATTEMPTS total times. Returns whether it eventually
 * succeeded — never throws (a failure here doesn't undo the AGD_Coupons row
 * already created, per the plan's write-ordering decision).
 */
async function postBundleUseRow(partyId: string, row: BundlePurchaseRow, couponCode: string): Promise<boolean> {
  const data = {
    $type: "Asi.Soa.Core.DataContracts.GenericEntityData, Asi.Contracts",
    EntityTypeName: "AGD_BundleUse",
    PrimaryParentEntityTypeName: "Party",
    Properties: {
      $type: "Asi.Soa.Core.DataContracts.GenericPropertyDataCollection, Asi.Contracts",
      $values: [
        property("ID", partyId),
        property("OrderNumber", row.OrderNumber),
        property("LineNumber", row.LineNumber),
        property("OrderDate", row.CreatedOn ?? new Date().toISOString()),
        property("CouponCode", couponCode),
        property("CouponMessage", row.CouponMessage),
      ],
    },
  };

  for (let attempt = 1; attempt <= BUNDLE_USE_WRITE_ATTEMPTS; attempt++) {
    const res = await iMISFetch<any>("AGD_BundleUse", { method: "POST", data: JSON.stringify(data) });
    if (res.success) return true;
  }
  return false;
}

/**
 * POSTs one AGD_Coupons row per input row (independent per-row POSTs, no
 * transaction/rollback — same pattern as AGD_CouponUsev2 writes in order.ts).
 * ProductCode is deliberately NOT set, matching the nightly SQL job's own
 * INSERT column list. A row whose ProductCode has no MaxUses mapping is
 * skipped (logged via console.warn) and counted as failed, since an
 * unrecognized code most likely means a new bundle SKU shipped without
 * updating MAX_USES_BY_PRODUCT_CODE.
 *
 * On a successful AGD_Coupons insert, also writes a matching AGD_BundleUse
 * row (with retry — see postBundleUseRow) so future checks recognize this
 * order line as claimed. AGD_Coupons is written first, deliberately: if the
 * AGD_BundleUse write then fails even after retries, the row is still
 * counted as added (the coupon is real and usable) rather than failed — the
 * alternative write order risks a claim that's marked done but never
 * produced a coupon, which nothing would ever retry
 * (docs/superpowers/specs/2026-07-22-bundle-use-idempotency-design.md,
 * "Write ordering and partial-failure handling").
 */
export async function claimBundleCoupons(
  partyId: string,
  rows: BundlePurchaseRow[]
): Promise<{ added: number; failed: number }> {
  let added = 0;
  let failed = 0;

  for (const row of rows) {
    const maxUses = MAX_USES_BY_PRODUCT_CODE[row.ProductCode];
    if (maxUses === undefined) {
      console.warn(`claimBundleCoupons: no MaxUses mapping for ProductCode "${row.ProductCode}", skipping`);
      failed += 1;
      continue;
    }

    const couponCode = generateCouponCode();
    const data = {
      $type: "Asi.Soa.Core.DataContracts.GenericEntityData, Asi.Contracts",
      EntityTypeName: "AGD_Coupons",
      PrimaryParentEntityTypeName: "Party",
      Properties: {
        $type: "Asi.Soa.Core.DataContracts.GenericPropertyDataCollection, Asi.Contracts",
        $values: [
          property("ID", partyId),
          property("CouponCode", couponCode),
          property("CreatedOn", new Date().toISOString()),
          property("CouponMessage", row.CouponMessage),
          property("CouponType", row.CouponType),
          property("Inactive", false),
          property("MaxUses", maxUses),
          property("ProductType", row.ProductType),
          property("CouponValue", 0),
          property("ExpiryDate", row.ExpiryDate),
        ],
      },
    };

    const res = await iMISFetch<any>("AGD_Coupons", { method: "POST", data: JSON.stringify(data) });
    if (!res.success) {
      failed += 1;
      continue;
    }

    added += 1;

    const bundleUseOk = await postBundleUseRow(partyId, row, couponCode);
    if (!bundleUseOk) {
      console.warn(
        `claimBundleCoupons: AGD_Coupons row created (CouponCode ${couponCode}) but AGD_BundleUse write failed after ${BUNDLE_USE_WRITE_ATTEMPTS} attempts for order ${row.OrderNumber} line ${row.LineNumber}`
      );
    }
  }

  return { added, failed };
}
